// /settings/agent — the Agent group's General row (docs/09 §4): bound to the
// working `?agent=` scope. Rename (PATCH /v1/agents/{id}, display_name), the
// agent ID with copy, read-only team + timestamps, and the archive danger
// action (DELETE /v1/agents/{id}) reusing the Agent hub's §2.12 name-match
// confirm. Under All agents it renders the standard agent-required empty
// state. Settings archetype (§4c): config-width Sections with per-section
// save. No field renders without a backing /v1 API field (UI-truthfulness).
import type { SyntheticEvent } from "react";
import { useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import { queryKeys } from "../../lib/queryKeys";
import { useWorkingScope } from "../../lib/workingScope";
import {
  Button,
  Copyable,
  EmptyState,
  Inline,
  InlineError,
  KeyValue,
  Page,
  Popover,
  Section,
  Skeleton,
  Stack,
  Text,
  TextInput,
} from "../../ui";
import { ArchiveAgentConfirm } from "../agent/AgentOverview";
import { useAgentActions } from "../agent/useAgentActions";

type AgentResponse = components["schemas"]["AgentResponse"];

export function AgentGeneralSettings() {
  const scope = useWorkingScope();
  const agentId = scope.agentId;
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const agent = useQuery({
    enabled: Boolean(agentId),
    queryKey: queryKeys.agents.detail(agentId ?? "pending"),
    queryFn: async () => {
      if (!agentId) {
        throw new Error("Agent id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/agents/{id}", {
        params: { path: { id: agentId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  // Read-only team name for the Details rows (docs/09 §4).
  const teamId = agent.data?.team_id;
  const team = useQuery({
    enabled: Boolean(teamId),
    queryKey: queryKeys.teams.detail(teamId ?? "pending"),
    queryFn: async () => {
      if (!teamId) {
        throw new Error("Team id is required.");
      }
      const { data, error } = await apiClient.GET("/v1/teams/{id}", {
        params: { path: { id: teamId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  // ASSUMED(post-archive stays on /settings/agent: the scope clears to All
  // agents and the row renders its standard agent-required empty state).
  const { archiveAgent, renameAgent } = useAgentActions({
    agentId,
    onArchived: () => {
      scope.clearAgent(teamId);
    },
    onCloseArchiveConfirm: () => {
      setShowArchiveConfirm(false);
    },
    // Rename here is a per-section form, not a popover — nothing to close.
    onCloseRenameForm: () => undefined,
  });

  return (
    <AgentGeneralSettingsView
      agent={agent.data ?? null}
      archiveError={
        archiveAgent.isError
          ? requestErrorDescription(archiveAgent.error)
          : null
      }
      hasAgentScope={agentId !== undefined}
      isArchivePending={archiveAgent.isPending}
      isLoading={agent.isPending && Boolean(agentId)}
      isRenamePending={renameAgent.isPending}
      loadError={agent.isError ? agent.error.message : null}
      onArchive={() => {
        archiveAgent.mutate();
      }}
      onCloseArchiveConfirm={() => {
        setShowArchiveConfirm(false);
      }}
      onRename={async (displayName) => {
        await renameAgent.mutateAsync(displayName);
      }}
      onStartArchiveConfirm={() => {
        setShowArchiveConfirm(true);
      }}
      showArchiveConfirm={showArchiveConfirm}
      teamName={
        team.data !== undefined && team.data.id === teamId
          ? team.data.display_name
          : undefined
      }
    />
  );
}

export interface AgentGeneralSettingsViewProps {
  agent: AgentResponse | null;
  archiveError: string | null;
  hasAgentScope: boolean;
  isArchivePending: boolean;
  isLoading: boolean;
  isRenamePending: boolean;
  loadError: string | null;
  onArchive: () => void;
  onCloseArchiveConfirm: () => void;
  onRename: (displayName: string) => Promise<void>;
  onStartArchiveConfirm: () => void;
  showArchiveConfirm: boolean;
  teamName?: string;
}

export function AgentGeneralSettingsView({
  agent,
  archiveError,
  hasAgentScope,
  isArchivePending,
  isLoading,
  isRenamePending,
  loadError,
  onArchive,
  onCloseArchiveConfirm,
  onRename,
  onStartArchiveConfirm,
  showArchiveConfirm,
  teamName,
}: AgentGeneralSettingsViewProps) {
  const archiveAnchorRef = useRef<HTMLButtonElement>(null);

  if (!hasAgentScope) {
    // The standard agent-required empty state (docs/09 §4; the Agent hub's
    // pattern) — All agents has no agent-bound settings to render.
    return (
      <Page variant="config">
        <Section>
          <EmptyState title="No agent selected" />
        </Section>
      </Page>
    );
  }

  return (
    <Page variant="config">
      <Stack gap={6}>
        {isLoading ? (
          <Skeleton
            label="Loading agent settings"
            rows={4}
            shape="detail-field"
          />
        ) : null}
        {loadError ? <InlineError>{loadError}</InlineError> : null}
        {agent ? (
          <>
            <Section heading="General">
              <AgentDisplayNameForm
                agent={agent}
                isPending={isRenamePending}
                onRename={onRename}
              />
            </Section>
            <Section heading="Details">
              <KeyValue
                rows={[
                  {
                    label: "Agent ID",
                    value: <Copyable label="Agent ID" value={agent.id} />,
                  },
                  {
                    label: "Team",
                    value: teamName ?? (
                      <Copyable label="Team ID" value={agent.team_id} />
                    ),
                  },
                  { label: "Created", value: formatDateTime(agent.created_at) },
                  { label: "Updated", value: formatDateTime(agent.updated_at) },
                ]}
              />
            </Section>
            <Section heading="Archive">
              <Stack gap={3}>
                <Text variant="muted">
                  Hides the agent from selection. Transcripts and Markers are
                  kept.
                </Text>
                <div className="marker-popover-anchor">
                  <Button
                    ref={archiveAnchorRef}
                    disabled={isArchivePending}
                    type="button"
                    variant="danger"
                    onClick={onStartArchiveConfirm}
                  >
                    Archive agent
                  </Button>
                  <Popover
                    anchorRef={archiveAnchorRef}
                    aria-label="Archive agent"
                    isOpen={showArchiveConfirm}
                    size="wide"
                    onDismiss={onCloseArchiveConfirm}
                  >
                    <ArchiveAgentConfirm
                      agent={agent}
                      error={archiveError}
                      isPending={isArchivePending}
                      onArchive={onArchive}
                      onCancel={onCloseArchiveConfirm}
                    />
                  </Popover>
                </div>
              </Stack>
            </Section>
          </>
        ) : null}
      </Stack>
    </Page>
  );
}

interface AgentDisplayNameFormProps {
  agent: AgentResponse;
  isPending: boolean;
  onRename: (displayName: string) => Promise<void>;
}

// Per-section save (§4c Settings archetype), mirroring the hub's rename
// rules: save disabled while empty, unchanged, or pending.
function AgentDisplayNameForm(props: AgentDisplayNameFormProps) {
  return <AgentDisplayNameFormContent key={props.agent.id} {...props} />;
}

function AgentDisplayNameFormContent({
  agent,
  isPending,
  onRename,
}: AgentDisplayNameFormProps) {
  const [displayName, setDisplayName] = useState(agent.display_name);
  const [localError, setLocalError] = useState<string | null>(null);
  const normalizedName = displayName.trim();
  const isDisabled =
    isPending ||
    normalizedName.length === 0 ||
    normalizedName === agent.display_name;

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isDisabled) {
      return;
    }
    setLocalError(null);
    try {
      await onRename(normalizedName);
    } catch (caught) {
      setLocalError(requestErrorDescription(caught));
    }
  }

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
    >
      <Stack gap={3}>
        <Inline align="end" className="marker-field-row" gap={3}>
          <TextInput
            label="Display name"
            required
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.currentTarget.value);
            }}
          />
          <Button disabled={isDisabled} type="submit">
            {isPending ? "Saving..." : "Save"}
          </Button>
        </Inline>
        {localError ? <InlineError>{localError}</InlineError> : null}
      </Stack>
    </form>
  );
}
